import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import Select from "react-select";
import RightMenubar from "../../components/subComponents/RightMenubar";
import { ConnectBreadcrumbBar } from "../../components/subComponents/ConnctBreadcrumb";
import ConnectFooter from "../../components/subComponents/ConnectFooter";
import { saffronURL } from "../../Url";

const DEFAULT_FILTERS = {
  country: "in",
  state: "",
  city: "",
  language: "en",
  category: "gn",
};

const ConnectingHinduHome = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const countryParam = searchParams.get("country");
  const stateParam = searchParams.get("state");
  const cityParam = searchParams.get("city");
  const languageParam = searchParams.get("language");
  const categoryParam = searchParams.get("category");
  const [selectedCountry, setSelectedCountry] = useState(
    searchParams.get("country") || DEFAULT_FILTERS.country,
  );
  const [selectedState, setSelectedState] = useState(
    stateParam || DEFAULT_FILTERS.state,
  );

  const [selectedCity, setSelectedCity] = useState(
    cityParam || DEFAULT_FILTERS.city,
  );

  const [selectedLanguage, setSelectedLanguage] = useState(
    searchParams.get("language") || DEFAULT_FILTERS.language,
  );
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get("category") || DEFAULT_FILTERS.category,
  );

  const { data, isPending, error } = useQuery({
    queryKey: ["connect", selectedCountry],
    queryFn: async () => {
      const res = await axios.get(
        `${saffronURL}/api/v1/public/selectors`,
        {
          params: {
            country_id: selectedCountry,
          },
        },
      );
      return res.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 1000 * 60 * 60, // 1 hour
    cacheTime: 1000 * 60 * 60 * 6, // 6 hours
  });

  const { data: statesData, isPending: loadingStates } = useQuery({
    queryKey: ["states", selectedCountry],
    queryFn: async () => {
      if (!selectedCountry) return {};
      const res = await axios.get(
        `${saffronURL}/api/v1/public/states/${selectedCountry}`,
      );
      return res.data;
    },
    enabled: !!selectedCountry, // 🔥 runs only when country exists
    staleTime: 1000 * 60 * 60,
  });

  const filterStateId = Object.entries(statesData?.data || {}).find(
    ([id, name]) => name === selectedState,
  )?.[0];

  const { data: citiesData, isPending: loadingCities } = useQuery({
    queryKey: ["cities", filterStateId],
    queryFn: async () => {
      if (!selectedState) return {};
      const res = await axios.get(
        `${saffronURL}/api/v1/public/cities/${filterStateId}?country_code=${selectedCountry}`,
      );
      return res.data;
    },
    enabled: !!filterStateId, // 🔥 runs only when state exists
    staleTime: 1000 * 60 * 60,
  });

  const languages = data?.data?.languages ?? {};
  const categories = data?.data?.categories ?? {};

  const countryOptions =
    data?.data?.countries &&
    Object.entries(data.data.countries).map(([value, label]) => ({
      value,
      label,
    }));

  const stateOptions =
    statesData?.data &&
    Object.entries(statesData.data).map(([value, label]) => ({
      value: label,
      label,
    }));

  const cityOptions =
    citiesData?.data &&
    Object.entries(citiesData.data).map(([value, label]) => ({
      value: label,
      label,
    }));
  const languageOptions = Object.entries(languages).map(([value, label]) => ({
    value,
    label,
  }));

  const categoryOptions = Object.entries(categories).map(([value, label]) => ({
    value,
    label,
  }));

  const { data: footer = [] } = useQuery({
    queryKey: [
      "connect_footer",
      selectedCountry,
      selectedCategory,
      selectedLanguage,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedCity) params.append("city", selectedCity);
      const res = await axios.get(
        `${saffronURL}/api/v1/public/footer?${params.toString()}`,
      );
      return res.data;
    },
    retry: false,
    staleTime: 1000 * 60 * 60,
  });

  const updateParam = (key, value, resetTree = true) => {
    // setPath([]);
    const params = new URLSearchParams(searchParams);

    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    if (resetTree) {
      params.delete("path");
      // setPath([]);
    }
    setSearchParams(params);
  };

  useEffect(() => {
    setSelectedCountry(countryParam ?? DEFAULT_FILTERS.country);
    setSelectedState(stateParam ?? DEFAULT_FILTERS.state); // ✅ clear when null
    setSelectedCity(cityParam ?? DEFAULT_FILTERS.city); // ✅ clear when null
    setSelectedLanguage(languageParam ?? DEFAULT_FILTERS.language);
    setSelectedCategory(categoryParam ?? DEFAULT_FILTERS.category);
  }, [countryParam, stateParam, cityParam, languageParam, categoryParam]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    let changed = false;

    if (!params.get("country")) {
      params.set("country", DEFAULT_FILTERS.country);
      changed = true;
    }
    if (!params.get("state")) {
      params.set("state", DEFAULT_FILTERS.state);
      changed = true;
    }
    if (!params.get("city")) {
      params.set("city", DEFAULT_FILTERS.city);
      changed = true;
    }

    if (!params.get("language")) {
      params.set("language", DEFAULT_FILTERS.language);
      changed = true;
    }

    if (!params.get("category")) {
      params.set("category", DEFAULT_FILTERS.category);
      changed = true;
    }

    if (changed) {
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // split path
    const parts = location.pathname.split("/").filter(Boolean);

    if (parts.length === 1) {
      navigate("home", { replace: true });
    }
  }, [location.pathname, navigate]);












  const mobileSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "32px",
      height: "32px",
      fontSize: "12px",

      // 🔥 preserve original styling
      backgroundColor: base.backgroundColor,
      borderColor: state.isFocused ? base.borderColor : base.borderColor,
      boxShadow: state.isFocused ? base.boxShadow : "none",
      "&:hover": {
        borderColor: base.borderColor,
      },
    }),

    valueContainer: (base) => ({
      ...base,
      padding: "0 6px",
    }),

    indicatorsContainer: (base) => ({
      ...base,
      height: "32px",
    }),

    dropdownIndicator: (base) => ({
      ...base,
      padding: "4px",
    }),

    clearIndicator: (base) => ({
      ...base,
      padding: "4px",
    }),

    menu: (base) => ({
      ...base,
      fontSize: "12px",
    }),
  };

  return (
    <>
      <div
        className="max-lg:pb-[75px] connect select-none"
        onContextMenu={(e) => e.preventDefault()}
        onCopy={(e) => e.preventDefault()}
        onPaste={(e) => e.preventDefault()}
        onCut={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      >
        <div className="min-h-screen  relative lg:grid lg:grid-cols-[1fr_88px] xl:grid-cols-[1fr_75px] bg-[#ffebda]">
          {/* Filter Dropdowns */}
          <div className=" min-w-0 bg-[#ffebda]">
            <div className=" flex justify-between items-center flex-wrap py-2 px-2 bg-[#ffebda]">
              <div className=" hidden lg:block"></div>

              <div className="z-50 w-full ">
                {/* ✅ Desktop (md+): same as before */}
                <div className="hidden lg:flex lg:flex-wrap justify-end gap-2 max-lg:w-full">
                  {/* Country */}
                  <Select
                    classNamePrefix="custom-select"
                    options={countryOptions}
                    value={countryOptions?.find(
                      (c) => c.value === selectedCountry,
                    )}
                    onChange={(val) => {
                      const country = val?.value || "";
                      setSelectedCountry(country);
                      setSelectedState("");
                      setSelectedCity("");

                      const params = new URLSearchParams(searchParams);
                      params.set("country", country);
                      params.delete("state");
                      params.delete("city");
                      params.delete("path");
                      setSearchParams(params);
                    }}
                    placeholder="Country"
                    isSearchable
                    styles={{
                      container: (base) => ({
                        ...base,
                        minWidth: "197px",
                        marginTop: "2px",
                      }),
                    }}
                  />

                  {/* State */}
                  <Select
                    classNamePrefix="custom-select"
                    options={stateOptions}
                    value={
                      stateOptions?.find((c) => c.value === selectedState) ||
                      null
                    }
                    onChange={(val) => {
                      const state = val?.value || "";
                      setSelectedState(state);

                      setSelectedCity("");

                      const params = new URLSearchParams(searchParams);
                      params.set("state", state);
                      params.delete("city");
                      params.delete("path");
                      setSearchParams(params);
                    }}
                    placeholder="State"
                    isLoading={loadingStates}
                    isSearchable
                    isClearable
                    isDisabled={!selectedCountry}
                    styles={{
                      container: (base) => ({
                        ...base,
                        minWidth: "197px",
                        marginTop: "2px",
                      }),
                    }}
                  />

                  {/* City */}
                  <Select
                    classNamePrefix="custom-select"
                    options={cityOptions}
                    value={
                      cityOptions?.find((c) => c.value === selectedCity) || null
                    }
                    onChange={(val) => {
                      const city = val?.value || "";
                      setSelectedCity(city);
                      updateParam("city", city);
                    }}
                    placeholder="City"
                    isSearchable
                    isClearable
                    styles={{
                      container: (base) => ({
                        ...base,
                        minWidth: "197px",
                        marginTop: "2px",
                      }),
                    }}
                  />

                  {/* Language */}
                  <Select
                    classNamePrefix="custom-select"
                    options={languageOptions}
                    value={languageOptions.find(
                      (l) => l.value === selectedLanguage,
                    )}
                    onChange={(val) => {
                      setSelectedLanguage(val.value);
                      updateParam("language", val.value);
                    }}
                    placeholder="Language"
                    isSearchable
                    styles={{
                      container: (base) => ({
                        ...base,
                        minWidth: "150px",
                        marginTop: "2px",
                      }),
                    }}
                  />

                  {/* Category */}
                  <Select
                    classNamePrefix="custom-select"
                    options={categoryOptions}
                    value={categoryOptions.find(
                      (c) => c.value === selectedCategory,
                    )}
                    onChange={(val) => {
                      setSelectedCategory(val.value);
                      updateParam("category", val.value);
                    }}
                    placeholder="Category"
                    isSearchable
                    styles={{
                      container: (base) => ({
                        ...base,
                        minWidth: "100px",
                        marginTop: "2px",
                      }),
                    }}
                  />
                </div>

                {/* ✅ Mobile (below md): 3 + 2 layout */}
                <div className="lg:hidden">
                  {/* Row 1: Country, Language, Category */}
                  <div className="grid grid-cols-3 gap-2">
                    <Select
                      classNamePrefix="custom-select"
                      options={countryOptions}
                      value={countryOptions?.find(
                        (c) => c.value === selectedCountry,
                      )}
                      onChange={(val) => {
                        const country = val?.value || "";
                        setSelectedCountry(country);
                        setSelectedState("");
                        setSelectedCity("");

                        const params = new URLSearchParams(searchParams);
                        params.set("country", country);
                        params.delete("state");
                        params.delete("city");
                        params.delete("path");
                        setSearchParams(params);
                      }}
                      placeholder="Country"
                      isSearchable
                      styles={{
                        ...mobileSelectStyles,
                        container: (base) => ({
                          ...base,
                          width: "100%",
                          minWidth: 0,
                        }),
                      }}
                    />

                    <Select
                      classNamePrefix="custom-select"
                      options={languageOptions}
                      value={languageOptions.find(
                        (l) => l.value === selectedLanguage,
                      )}
                      onChange={(val) => {
                        setSelectedLanguage(val.value);
                        updateParam("language", val.value);
                      }}
                      placeholder="Language"
                      isSearchable
                      styles={{
                        ...mobileSelectStyles,
                        container: (base) => ({
                          ...base,
                          width: "100%",
                          minWidth: 0,
                        }),
                      }}
                    />

                    <Select
                      classNamePrefix="custom-select"
                      options={categoryOptions}
                      value={categoryOptions.find(
                        (c) => c.value === selectedCategory,
                      )}
                      onChange={(val) => {
                        setSelectedCategory(val.value);
                        updateParam("category", val.value);
                      }}
                      placeholder="Category"
                      isSearchable
                      styles={{
                        ...mobileSelectStyles,
                        container: (base) => ({
                          ...base,
                          width: "100%",
                          minWidth: 0,
                        }),
                      }}
                    />
                  </div>

                  {/* Row 2: State, City */}
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <Select
                      classNamePrefix="custom-select"
                      options={stateOptions}
                      value={
                        stateOptions?.find((c) => c.value === selectedState) ||
                        null
                      }
                      onChange={(val) => {
                        const state = val?.value || "";
                        setSelectedState(state);

                        setSelectedCity("");

                        const params = new URLSearchParams(searchParams);
                        params.set("state", state);
                        params.delete("city");
                        params.delete("path");
                        setSearchParams(params);
                      }}
                      placeholder="State"
                      isLoading={loadingStates}
                      isSearchable
                      isClearable
                      isDisabled={!selectedCountry}
                      styles={{
                        ...mobileSelectStyles,
                        container: (base) => ({
                          ...base,
                          width: "100%",
                          minWidth: 0,
                        }),
                      }}
                    />

                    <Select
                      classNamePrefix="custom-select"
                      options={cityOptions}
                      value={
                        cityOptions?.find((c) => c.value === selectedCity) ||
                        null
                      }
                      onChange={(val) => {
                        const city = val?.value || "";
                        setSelectedCity(city);
                        updateParam("city", city);
                      }}
                      placeholder="City"
                      isSearchable
                      isClearable
                      styles={{
                        ...mobileSelectStyles,
                        container: (base) => ({
                          ...base,
                          width: "100%",
                          minWidth: 0,
                        }),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Breadcrumb */}
            {/* <ConnectBreadcrumbBar crumbs={crumbs} /> */}
            <div className=" ">
              <Outlet
                context={{
                  selectedCountry,
                  selectedState,
                  selectedCity,
                  selectedLanguage,
                  selectedCategory,
                  setSelectedCountry,
                  setSelectedState,
                  setSelectedCity,
                  setSelectedLanguage,
                  setSelectedCategory,
                  updateParam,
                }}
              />
            </div>
          </div>

          <RightMenubar
            selectedCountry={selectedCountry}
            selectedState={selectedState}
            selectedCity={selectedCity}
            selectedLanguage={selectedLanguage}
            selectedCategory={selectedCategory}
            searchParams={searchParams}
          />
        </div>
        <ConnectFooter data={footer?.data?.footer} />
      </div>
    </>
  );
};

export default ConnectingHinduHome;
